/**
 * EventCreateWizard — unified EIB event-creation wizard (CREATE ONLY).
 *
 * Redesign source of truth: docs/EIB_EVENT_CREATION_WIZARD_REDESIGN_R2.md
 * ("LOCKED PLAN"). Replaces the old flow of (bare EventForm → navigate to
 * `/events/:id?setup=1&mode=` → EIB modal re-open) with ONE continuous stepped
 * flow:
 *
 *   1. Basics      — image, title, description, event type
 *   2. When & where — date/time (single vs multi-day by type), timezone,
 *                     location OR promoter org (gun-show swap), regulatory state
 *   3. Team        — sales hierarchy (+ non-gun-show promoter selector). (Item 5, 2026-08-14:
 *      the Team step moved into CREATE as step 3; "Configure Event" resumes straight into the
 *      EIB setup below, no Team step there.)
 *      → THE EVENT IS CREATED HERE as `scheduled` (Saved incomplete) at the end of the basics
 *        steps. No draft, ever.
 *   4-7,9. Reward / Registration & capacity / Audience / Invite & reminders /
 *          Preview & Launch — REUSED wholesale from <EventInABoxWizard/> mounted
 *          inline against the created event (Configure Event resumes here; no re-open).
 *   8. CTA / QR   — prepare the event's subscription CTA ($20 + QR), tracking codes copied from
 *                   the promoter/host (EventCTAInitPanel; same panel as the Call to Action tab).
 *
 * IMPORTANT: EventForm.tsx remains the EDIT surface (EventEdit.tsx) and is NOT
 * touched by this wizard. This component is create-only.
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import ConfirmDialog from "../ui/ConfirmDialog";
import {
  datesInclusive,
  getTimeOffConflicts,
  timeOffSentence,
} from "../../services/timeOffConflicts";
import { getEventSalesAssignments as listEventSalesReps } from "../../services/salesCalendar.service";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeftIcon, CheckCircleIcon } from "@heroicons/react/24/outline";

import { supabase } from "../../services/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../ui/Toast";
import { useCreateEvent } from "../../hooks/useEvents";
import {
  saveEventOrgLocations,
  notifyEventNeedsConfiguration,
  type CreateEventInput,
} from "../../services/events.service";
import { afterEventLinksSaved } from "../../services/eventCTAAutoPrepare.service";
import {
  leadRepSyncNotice,
  syncLeadRepToSalesReps,
} from "../../services/salesCalendar.service";
import EventCTAInitPanel from "./EventCTAInitPanel";
import { isFeatureEnabled } from "../../utils/featureFlags";
import {
  eventTypeHasNoCtaQr,
  eventTypeIsNoPromo,
  eventTypeNeedsSetup,
} from "../../utils/eventTypePolicy";

import {
  US_STATES,
  EVENT_TYPE_CLASSIFICATION,
  SUBSCRIPTION_ELIGIBLE_EVENT_TYPES,
} from "../../types/event.types";
import type { Event } from "../../types/event.types";
import { EVENT_TYPES, VISIBLE_EVENT_TYPES } from "../../utils/constants";
import {
  FIELD_MANAGER_HIDDEN_EVENT_TYPES,
  fieldManagerAllows,
  isFieldManagerRole,
  type FieldManagerPositionScope,
} from "../../utils/fieldManagerAccess";
import {
  eventVenueError,
  venueLinkRequired,
} from "../../utils/eventVenueRequirement";

import Button from "../common/Button";
import Input from "../common/Input";
import DateRangePicker, {
  type DateRange,
  validateDateRange,
} from "./DateRangePicker";
import TimezoneSelector, {
  getTimezoneOffset,
} from "../common/TimezoneSelector";
import SalesHierarchyChainNote from "./SalesHierarchyChainNote";
import SalesHierarchyFields, {
  type HierarchyChain,
  type SalesPositionRow,
} from "./SalesHierarchyFields";
import {
  fillFromLeadChain,
  type ManagerLevels,
} from "../../utils/repReportingChain";
import {
  autoEventTimezone,
  describeTimezoneSource,
  timezoneUnsetNote,
  type AutoEventTimezone,
} from "../../utils/zipTimezone";
import EventOrganizationLocationSelector, {
  type EventOrgLocationSelection,
} from "./EventOrganizationLocationSelector";
import PromoterSelector from "./PromoterSelector";
import { canAddUnsignedPromoter } from "../../services/unsignedPromoter.service";
import EventInABoxWizard from "./EventInABoxWizard";
import { teamPositionError } from "../../utils/eventTeamPosition";
import {
  leadRequiredError,
  leadSoftWarning,
  useEventLeadRequired,
} from "../../utils/eventLeadRequired";
import EventDailyHoursEditor, {
  EMPTY_DAILY_HOURS,
  planDayHoursSave,
  type DailyHoursValue,
} from "./EventDailyHoursEditor";
import {
  dailyHoursApplicabilityLocal,
  type DayHours,
} from "../../utils/eventDayHours";
import {
  describeDayHoursError,
  setEventDayHours,
} from "../../services/eventDayHours.service";
import {
  resolveLeadRepPositionId,
  planLeadRepSave,
} from "../../utils/eventSelfService";

// "Gun Show - Not Advertised" (RM/TM role setup REQ-2.4, owner 2026-10-29): a temporary (~90 days) entry in the
// Event Type list. It is a gun show with events.not_advertised = true (migration 1242: no Event in a Box invitations,
// no published landing page, still listed publicly and counted as a gun show), so removing it later is deleting this
// option. Offered to everyone who can create a gun show.
const GUN_SHOW_NOT_ADVERTISED = "gun_show__not_advertised";
const GUN_SHOW_NOT_ADVERTISED_LABEL = "Gun Show – Not Advertised";

// Event types that span multiple calendar days (need a real start+end DATE, not
// just start/end times on one day). Everything else is single-day. Kept as a
// small local list — the full pre-registration matrix is deferred (see LOCKED
// PLAN); this is only about the date model.
const MULTI_DAY_TYPES = new Set<string>(["gun_show", "conference"]);

type Phase = "basics" | "setup" | "cta";
type BasicsStep = "basics" | "when" | "team";
const BASICS_ORDER: BasicsStep[] = ["basics", "when", "team"];

const EventCreateWizard: React.FC<{ resumeEventId?: string }> = ({
  resumeEventId,
}) => {
  // Resume ("Configure Event"): the wizard was re-opened for an already-created event to run the
  // EIB setup (Workflow onward). The initial create flow collects Basics + When & Where + Team
  // (item 5), then Saves the event as setup_incomplete.
  const isResume = !!resumeEventId;
  const navigate = useNavigate();
  const { user } = useAuth();
  // RM/TM (owner 2026-10-29): they create events but never configure them: the wizard always ends at "Save" for them,
  // never enters the Event in a Box setup, and a resumed "Configure Event" link sends them to the event instead.
  const fieldManager = isFieldManagerRole(user?.role);
  const { showToast } = useToast();
  const createMutation = useCreateEvent();

  // -----------------------------------------------------------------------
  // Flow state
  // -----------------------------------------------------------------------
  const [phase, setPhase] = useState<Phase>("basics");
  const [basicsStep, setBasicsStep] = useState<BasicsStep>(
    resumeEventId ? "team" : "basics",
  );
  // Steps reachable in this entry mode: create collects only Basics + When &
  // Where (then Save → incomplete); configure runs the Team step (then → EIB
  // setup). Everything from step 3 onward lives in the configure flow.
  // Item 5: the Team (sales hierarchy) step moved INTO the Create flow as step 3. Create now
  // collects Basics + When & Where + Team, then Saves (incomplete). Configure ("Configure Event")
  // no longer shows Team — it resumes straight into the EIB setup (Workflow onward).
  const reachableBasics: BasicsStep[] = isResume
    ? []
    : ["basics", "when", "team"];
  const [createdEvent, setCreatedEvent] = useState<Event | null>(null);
  // MSR activation fix 6: the create-time Lead -> Sales Reps list sync failed; re-entry retries that same rep.
  const leadSyncPendingRef = useRef(false);
  // 0-based index of the embedded EIB's current step (setup phase) → drives the
  // unified StepRail so the whole create flow reads as one continuous wizard.
  const [eibStepIndex, setEibStepIndex] = useState(0);
  // Total EIB steps reported by the embedded wizard: 6 normally, 3 for walk-in-only
  // (which hides Audience/Invitation/Review & Launch). Drives the unified rail so the
  // hidden steps disappear and the remaining steps renumber sequentially.
  const [eibStepTotal, setEibStepTotal] = useState(6);
  const [creating, setCreating] = useState(false);
  // True while the saved event's QR code and flyer are being made (after the save, before leaving).
  const [finishingCta, setFinishingCta] = useState(false);
  // Per-field validation errors for the current basics step, keyed by field.
  // ALL invalid fields on the step are collected at once (not first-only) and
  // surfaced inline under each field; a field's error clears as it's corrected.
  const [basicsErrors, setBasicsErrors] = useState<Record<string, string>>({});
  // Which basics steps have had a validation attempt (Next clicked). Once a step
  // is attempted, its inline errors are kept in sync by RE-VALIDATING against the
  // current normalized values (see effect below) — not by deleting errors merely
  // because a value changed. That's what makes a whitespace-only title, or
  // clearing Start Date (which also blanks the times), correctly KEEP the errors.
  const [attemptedSteps, setAttemptedSteps] = useState<Set<BasicsStep>>(
    new Set(),
  );
  // Stable client-generated primary key for the event we're about to create.
  // Generated ONCE (survives re-renders and retries) so that if a create commits
  // in the DB but its response is lost, retrying re-uses the SAME id and the
  // service recovers the existing row instead of creating a duplicate (audit MED).
  const eventIdRef = useRef<string>(crypto.randomUUID());

  // -----------------------------------------------------------------------
  // Basics form state (only the fields the redesign KEEPS for create)
  // -----------------------------------------------------------------------
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "",
    state: "", // regulatory state
    location_type: "physical" as "physical" | "virtual" | "hybrid",
    location_name: "",
    location_address_line1: "",
    location_city: "",
    location_state: "",
    location_postal_code: "",
    meeting_url: "",
    promoter_id: "",
    promoter_name: "",
    // Item 8: optional gun-show entry/participation fee — internal tracking + reimbursement only.
    entry_fee: "",
    // Item 4: max capacity (max_attendees). SOFT by default (informational); becomes a HARD cap that
    // blocks registrations/check-ins only when attendance_hard_cap is on. Blank = no cap.
    max_attendees: "",
    attendance_hard_cap: false,
    // Gun Show - Not Advertised (see GUN_SHOW_NOT_ADVERTISED); only ever true with type gun_show.
    not_advertised: false,
  });

  // Whether this event is pre-registration based. DERIVED from the selected
  // event type's classification (each type declares registration true/false).
  // The old page-1 "Pre-registration required?" toggle was removed as redundant
  // with the EIB "Workflow" step, which remains the authoritative control — this
  // only seeds a sensible default the operator can still switch on that step.
  const registrationRequired =
    EVENT_TYPE_CLASSIFICATION[formData.type]?.registration ?? false;

  // Date / timezone
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    allDay: false,
  });
  // EVT-08b (owner 2026-10-30): starts UNSET. It is filled only from a recognised venue ZIP
  // (never from the state or DEFAULT_TIMEZONE); otherwise the operator must choose one.
  const [timezone, setTimezone] = useState<string>("");
  const timezoneManuallySet = useRef(false);
  // Bumped when the operator picks the blank "Select timezone..." option, so the ZIP rule resumes.
  const [tzRederive, setTzRederive] = useState(0);

  // Hours each day (migration 2026-10-27-1120): optional per-date hours for a multi-day event, saved right after
  // the event is created. The applicability uses the same end rule as buildStartEnd (blank end = start).
  const [dailyHours, setDailyHours] =
    useState<DailyHoursValue>(EMPTY_DAILY_HOURS);
  const savedDayHoursRef = useRef<DayHours[]>([]);
  const dailyHoursApp = useMemo(
    () =>
      dailyHoursApplicabilityLocal({
        startDate: dateRange.startDate,
        startTime: dateRange.startTime,
        endDate: dateRange.endDate || dateRange.startDate,
        endTime: dateRange.endTime || dateRange.startTime,
        allDay: dateRange.allDay,
        timezone,
      }),
    [dateRange, timezone],
  );
  const saveDailyHours = async (eventId: string) => {
    const plan = planDayHoursSave(
      dailyHoursApp,
      dailyHours,
      savedDayHoursRef.current,
    );
    if (plan.action !== "set" && plan.action !== "clear") {
      return;
    }
    try {
      savedDayHoursRef.current = await setEventDayHours(
        eventId,
        plan.action === "set" ? plan.days : [],
      );
    } catch (dhErr) {
      console.warn("Saving daily hours failed:", dhErr);
      showToast(
        "error",
        `Event created, but the hours for each day were not saved: ${describeDayHoursError(dhErr)} You can set them by editing the event.`,
      );
    }
  };

  // Org / location associations (non-gun-show)
  const [orgLocationSelections, setOrgLocationSelections] = useState<
    EventOrgLocationSelection[]
  >([]);

  // Sales hierarchy
  const [hierarchyChain, setHierarchyChain] = useState<HierarchyChain>({
    repId: null,
    tmId: null,
    rmId: null,
    sdId: null,
  });
  const [isSelfService, setIsSelfService] = useState(false);
  const hierarchyManuallyOverridden = useRef(false);
  // RM / TM (internal audit round 16): the database accepts only a Lead/Event Rep from their team and manager levels
  // from their team or the chain above them, so the pickers offer only those and a prefilled (account-derived)
  // position outside that scope is cleared instead of failing the save.
  const { data: fieldManagerScope } = useQuery<FieldManagerPositionScope>({
    queryKey: ["field-manager-position-scope", user?.id],
    enabled: fieldManager && !!user?.id,
    queryFn: async () => {
      const [team, above] = await Promise.all([
        supabase.rpc("fm_scope_position_ids"),
        supabase.rpc("fm_ancestor_position_ids"),
      ]);
      if (team.error) {
        throw team.error;
      }
      if (above.error) {
        throw above.error;
      }
      return {
        team: new Set<string>((team.data as string[] | null) ?? []),
        above: new Set<string>((above.data as string[] | null) ?? []),
      };
    },
  });
  useEffect(() => {
    if (!fieldManagerScope) {
      return;
    }
    const keep = (slot: keyof HierarchyChain, id: string | null) =>
      fieldManagerAllows(fieldManagerScope, slot, id) ? id : null;
    const next: HierarchyChain = {
      repId: keep("repId", hierarchyChain.repId),
      tmId: keep("tmId", hierarchyChain.tmId),
      rmId: keep("rmId", hierarchyChain.rmId),
      sdId: keep("sdId", hierarchyChain.sdId),
    };
    if (
      next.repId !== hierarchyChain.repId ||
      next.tmId !== hierarchyChain.tmId ||
      next.rmId !== hierarchyChain.rmId ||
      next.sdId !== hierarchyChain.sdId
    ) {
      setHierarchyChain(next);
    }
  }, [fieldManagerScope, hierarchyChain]);
  // GS-06: account chain from the last derive — fallback only when the lead has no chain.
  const accountLevelsRef = useRef<ManagerLevels | null>(null);
  const managersHandEditedRef = useRef(false);
  // mig 1280: did the event being configured have a TM / RM / SD when loaded (resume flow); null for a new event
  const teamHadOneRef = useRef<boolean | null>(null);
  const previousFirstOrgIdRef = useRef<string | null>(null);
  const locationAutoFilledRef = useRef(false);

  const isGunShow = formData.type === "gun_show";
  const isMultiDay = MULTI_DAY_TYPES.has(formData.type);
  // Whether the venue/organization link is mandatory for the shape currently
  // selected. Drives the required marker + helper copy; the block itself is
  // enforced in validateBasicsStep("when"). The create wizard always writes
  // date_tbd: false, so only location type can exempt an event here.
  const venueLinkIsRequired = venueLinkRequired({
    locationType: formData.location_type,
    dateTbd: false,
    eventType: formData.type,
    notAdvertised: formData.not_advertised,
  });

  // -----------------------------------------------------------------------
  // Sales positions (for hierarchy dropdowns)
  // -----------------------------------------------------------------------
  const { data: salesPositions } = useQuery<SalesPositionRow[]>({
    // Distinct key per data shape (2026-10-16): the same key held different rows/columns on other
    // screens, and with the 5-minute staleTime whichever screen fetched first won.
    queryKey: ["sales-positions-active", "event-form-select"],
    queryFn: async () => {
      const { data } = await supabase
        .from("sales_positions")
        .select("id, title, assigned_name, position_type, parent_position_id")
        .eq("is_active", true)
        .order("assigned_name", { ascending: true, nullsFirst: false });
      return (data as SalesPositionRow[]) || [];
    },
  });

  // -----------------------------------------------------------------------
  // Resume / "Configure Event": hydrate the existing event and enter at Team.
  // Only the fields the remaining steps need are seeded — type (drives gun-show
  // /registration/subscription behaviour), promoter, the sales hierarchy (so a
  // re-configure never wipes a prior assignment), and createdEvent (its id/title
  // /start_time feed the embedded EIB). Org/location is NOT re-touched here — it
  // was captured at create and the Team step doesn't own it.
  // -----------------------------------------------------------------------
  const resumeHydratedRef = useRef(false);
  useEffect(() => {
    if (!resumeEventId || resumeHydratedRef.current) {
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("id", resumeEventId)
        .single();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        showToast("error", "Could not load this event to configure.");
        return;
      }
      resumeHydratedRef.current = true;
      const ev = data as Event;
      // Owner 2026-10-29: a class, table, seminar or speaking engagement has nothing to configure (no Event in a Box
      // invitation, landing page or event CTA / QR -- the server refuses them, migration 1260). A stale link or
      // bookmark to "Configure Event" goes back to the event instead of opening a setup that would be refused.
      if (eventTypeIsNoPromo(ev.type)) {
        showToast(
          "info",
          "This event type has no setup: it sends no invitations and uses the venue's QR codes.",
        );
        navigate(`/events/${ev.id}`, { replace: true });
        return;
      }
      eventIdRef.current = ev.id;
      setCreatedEvent(ev);
      const addr = (ev.location_address ?? {}) as Record<string, string>;
      setFormData((prev) => ({
        ...prev,
        title: ev.title ?? "",
        description: ev.description ?? "",
        type: ev.type ?? "",
        state: ev.state ?? "",
        location_type:
          (ev.location_type as typeof prev.location_type) ?? "physical",
        location_name: ev.location_name ?? "",
        location_address_line1: addr.address_line1 ?? "",
        location_city: addr.city ?? "",
        location_state: addr.state ?? "",
        location_postal_code: addr.postal_code ?? "",
        meeting_url: ev.meeting_url ?? "",
        promoter_id: ev.promoter_id ?? "",
        promoter_name: ev.promoter_name ?? "",
        entry_fee:
          ev.entry_fee !== null && ev.entry_fee !== undefined
            ? String(ev.entry_fee)
            : "",
        max_attendees:
          ev.max_attendees !== null && ev.max_attendees !== undefined
            ? String(ev.max_attendees)
            : "",
        attendance_hard_cap:
          (ev as { attendance_hard_cap?: boolean }).attendance_hard_cap ??
          false,
      }));
      // Rehydrate the sales hierarchy and mark it manually set so the org-derive
      // effect never overwrites what the operator previously chose.
      hierarchyManuallyOverridden.current = true;
      setIsSelfService(!!ev.is_self_service);
      teamHadOneRef.current = !!(
        ev.territory_manager_position_id ||
        ev.regional_manager_position_id ||
        ev.sales_director_position_id
      );
      setHierarchyChain({
        repId: ev.is_self_service ? null : (ev.sales_position_id ?? null),
        tmId: ev.territory_manager_position_id ?? null,
        rmId: ev.regional_manager_position_id ?? null,
        sdId: ev.sales_director_position_id ?? null,
      });
      // Item 5: Team is now collected during Create, so Configure resumes STRAIGHT into the EIB
      // setup (the created event is already loaded above) — no Team step here.
      if (fieldManager) {
        showToast(
          "info",
          "Event setup is done by the events team; Regional and Territory Managers view and run events.",
        );
        navigate(`/events/${ev.id}`, { replace: true });
        return;
      }
      setPhase("setup");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeEventId]);

  // -----------------------------------------------------------------------
  // Event-type list filtered by pre-registration only (matrix deferred)
  // -----------------------------------------------------------------------
  // All visible event types. The list is no longer gated by a page-1
  // pre-registration toggle (removed as redundant with the EIB Workflow step).
  // A legacy/current formData.type outside the visible set is still appended so
  // it's never silently dropped.
  // RM/TM (REQ-2.3): Conference / Expo / Trade Show, Networking Event, Member Check-in and Lightning Deals are not
  // theirs to create (the database refuses them too).
  const roleTypes = fieldManager
    ? VISIBLE_EVENT_TYPES.filter(
        (t) => !FIELD_MANAGER_HIDDEN_EVENT_TYPES.has(t.value),
      )
    : VISIBLE_EVENT_TYPES;
  const withNotAdvertised = roleTypes.flatMap((t) =>
    t.value === "gun_show"
      ? [
          t,
          {
            ...t,
            value: GUN_SHOW_NOT_ADVERTISED,
            label: GUN_SHOW_NOT_ADVERTISED_LABEL,
          },
        ]
      : [t],
  );
  const classifiedTypeOptions = (() => {
    if (
      !formData.type ||
      withNotAdvertised.some((t) => t.value === formData.type)
    ) {
      return withNotAdvertised;
    }
    return [
      ...withNotAdvertised,
      ...EVENT_TYPES.filter((t) => t.value === formData.type),
    ];
  })();

  // Collapse a previously-set end date when the type switches to single-day
  // (e.g. Conference → Class after an end date was chosen), so the saved event
  // never spans two days under a single-day type.
  useEffect(() => {
    if (isMultiDay) {
      return;
    }
    setDateRange((prev) =>
      prev.endDate && prev.endDate !== prev.startDate
        ? { ...prev, endDate: prev.startDate }
        : prev,
    );
  }, [isMultiDay]);

  // EVT-08b (owner 2026-10-30): set the time zone from the venue ZIP ONLY, unless the operator
  // chose one by hand. No ZIP / an unrecognised ZIP / a virtual event -> unset (""), with a note
  // asking for the postal code or a choice; Next and Save refuse a blank zone. A later ZIP change
  // re-derives (and a ZIP that no longer resolves clears the auto-filled zone) until the operator
  // picks a zone themselves; after that the ZIP never overrides their choice.
  const [tzAuto, setTzAuto] = useState<AutoEventTimezone | null>(null);
  useEffect(() => {
    if (timezoneManuallySet.current) {
      return;
    }
    const auto = autoEventTimezone({
      locationType: formData.location_type,
      postalCode: formData.location_postal_code,
    });
    setTimezone(auto.timezone);
    setTzAuto(auto);
  }, [formData.location_postal_code, formData.location_type, tzRederive]);

  // Derive hierarchy + autofill address from the first selected org.
  const deriveHierarchyForOrg = useCallback(async (organizationId: string) => {
    const { data, error } = await supabase.rpc("derive_account_hierarchy", {
      p_organization_id: organizationId,
    });
    if (error || !data || data.length === 0) {
      return null;
    }
    return data[0] as {
      assignment_found: boolean;
      sales_rep_position_id: string | null;
      territory_manager_position_id: string | null;
      regional_manager_position_id: string | null;
      director_position_id: string | null;
    };
  }, []);

  useEffect(() => {
    const firstSel = orgLocationSelections[0] ?? null;
    const firstOrgId = firstSel?.org_id ?? null;
    const firstLocationId = firstSel?.location_id ?? null;
    // Re-run when EITHER the org OR the chosen location changes, so picking a
    // specific location within the same org re-populates the venue fields.
    const selKey = firstOrgId ? `${firstOrgId}:${firstLocationId ?? ""}` : null;
    if (selKey === previousFirstOrgIdRef.current) {
      return;
    }
    // GS-06: wait for the positions so the rep-chain fill is not skipped (re-runs on load).
    if (firstOrgId && salesPositions === undefined) {
      return;
    }
    previousFirstOrgIdRef.current = selKey;
    if (!firstOrgId) {
      return;
    }
    (async () => {
      // Resolve the venue to auto-fill. Prefer the specifically chosen location,
      // else the org's single non-archived location (retailers keep their address
      // in a `locations` row, not organizations.address_*), else the org's own
      // address. This is why picking a retailer used to leave Location blank —
      // its address never lived on the organizations row.
      const [derived, locRow, orgAddrRow] = await Promise.all([
        deriveHierarchyForOrg(firstOrgId),
        (async () => {
          if (firstLocationId) {
            const { data } = await supabase
              .from("locations")
              .select("name, address_line1, city, state, postal_code")
              .eq("id", firstLocationId)
              .maybeSingle();
            return data;
          }
          // No specific location chosen — use the org's location only when it has
          // exactly one non-archived location (unambiguous). limit(2) distinguishes
          // "one" from "many" cheaply.
          const { data: locs } = await supabase
            .from("locations")
            .select("name, address_line1, city, state, postal_code")
            .eq("organization_id", firstOrgId)
            .eq("is_archived", false)
            .limit(2);
          return locs && locs.length === 1 ? locs[0] : null;
        })(),
        supabase
          .from("organizations")
          .select(
            "address_line1, address_city, address_state, address_postal_code",
          )
          .eq("id", firstOrgId)
          .single()
          .then((r) => r.data),
      ]);

      // Hierarchy — only auto-populate if the operator hasn't manually edited it
      // (simplified vs EventForm's confirm-modal; on a fresh create there is no
      // prior data worth a modal, and a manual edit is respected).
      if (derived?.assignment_found) {
        accountLevelsRef.current = {
          tmId: derived.territory_manager_position_id,
          rmId: derived.regional_manager_position_id,
          sdId: derived.director_position_id,
        };
      }
      if (derived?.assignment_found && !hierarchyManuallyOverridden.current) {
        // GS-06: managers follow the derived rep's reporting chain (account = fallback).
        setHierarchyChain({
          repId: derived.sales_rep_position_id,
          ...fillFromLeadChain(
            salesPositions ?? [],
            derived.sales_rep_position_id,
            accountLevelsRef.current ?? {
              tmId: null,
              rmId: null,
              sdId: null,
            },
            accountLevelsRef.current,
          ).levels,
        });
      }

      // Location autofill — prefer the resolved `locations` venue (incl. its name),
      // fall back to the org address (which carries no venue name). Only when the
      // operator hasn't typed a venue of their own; auto-filled values may be freely
      // edited afterward.
      const venue = locRow
        ? {
            name: locRow.name ?? "",
            line1: locRow.address_line1 ?? "",
            city: locRow.city ?? "",
            state: locRow.state ?? "",
            postal: locRow.postal_code ?? "",
          }
        : orgAddrRow
          ? {
              name: "",
              line1: orgAddrRow.address_line1 ?? "",
              city: orgAddrRow.address_city ?? "",
              state: orgAddrRow.address_state ?? "",
              postal: orgAddrRow.address_postal_code ?? "",
            }
          : null;

      if (venue) {
        setFormData((prev) => {
          const hasManualLocation =
            (prev.location_name || "").trim() !== "" ||
            (prev.location_address_line1 || "").trim() !== "" ||
            (prev.location_city || "").trim() !== "" ||
            (prev.location_state || "").trim() !== "" ||
            (prev.location_postal_code || "").trim() !== "";
          if (hasManualLocation && !locationAutoFilledRef.current) {
            return prev;
          }
          locationAutoFilledRef.current = true;
          return {
            ...prev,
            // Prepopulate the venue name: prefer a specific location's own name, else
            // the selected range/retailer's org name (so a retailer no longer leaves
            // Location Name blank). Gun shows are the exception — the promoter is not the
            // venue and shows move around, so leave it blank for the operator to fill.
            location_name:
              (isGunShow ? "" : venue.name || firstSel?.org_name || "") ||
              prev.location_name,
            location_address_line1: venue.line1,
            location_city: venue.city,
            location_state: venue.state,
            location_postal_code: venue.postal,
          };
        });
      }
    })();
  }, [orgLocationSelections, deriveHierarchyForOrg, salesPositions]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    if (
      name === "location_name" ||
      name === "location_address_line1" ||
      name === "location_city" ||
      name === "location_state" ||
      name === "location_postal_code"
    ) {
      // A manual edit to ANY venue field (incl. the name) disarms the auto-fill
      // guard so a later org/location change won't clobber the operator's input.
      locationAutoFilledRef.current = false;
    }
    // Inline errors are recomputed by the re-validation effect once the step is
    // attempted; selecting a Location State also populates the regulatory `state`,
    // and the effect clears both errors because both become valid.
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "location_state" && value) {
        updated.state = value;
      }
      return updated;
    });
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const notAdvertised = e.target.value === GUN_SHOW_NOT_ADVERTISED;
    const nextType = notAdvertised ? "gun_show" : e.target.value;
    setFormData((prev) => ({
      ...prev,
      type: nextType,
      not_advertised: notAdvertised,
    }));
    // Gun shows use the promoter slot (no org selector), so clear any org
    // selections made under a prior type to avoid invisible associations.
    if (nextType === "gun_show") {
      setOrgLocationSelections([]);
    }
  };

  // Single-day event types must not span two calendar days. The shared
  // DateRangePicker always exposes an end DATE, so we lock it here (in the
  // wizard, without touching the shared component that EventForm's edit path
  // also uses): for single-day types the end date is forced to equal the start
  // date; only the end TIME stays operator-editable. Multi-day types keep a real
  // end date.
  const handleDateRangeChange = (next: DateRange) => {
    // Date/time inline errors are recomputed by the re-validation effect from the
    // resulting values, so a change clears an error only when the field is actually
    // valid and restores it (incl. dependent times) when it is not.
    if (isMultiDay) {
      setDateRange(next);
      return;
    }
    setDateRange({ ...next, endDate: next.startDate });
  };

  // -----------------------------------------------------------------------
  // Per-step validation
  //
  // Collects EVERY invalid field on the step into a {fieldKey: message} map
  // (never early-returns) so all errors surface AT ONCE, inline under their
  // fields. Rules preserved exactly from the original first-only validator.
  // -----------------------------------------------------------------------
  // mig 1280: the server switch (system_config.event_team_position_required), read through its RPC; off on error
  const { data: teamPositionRequired = false } = useQuery({
    queryKey: ["event-team-position-required"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "event_team_position_required",
      );
      if (error) {
        return false;
      }
      return data === true;
    },
    staleTime: 60_000,
  });
  // mig 1630: Lead / Event Rep required (server switch event_lead_required)
  const leadRequired = useEventLeadRequired();
  const validateBasicsStep = (s: BasicsStep): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (s === "team") {
      const leadErr = leadRequiredError(
        leadRequired,
        hierarchyChain.repId,
        isSelfService,
      );
      if (leadErr) {
        errs.lead = leadErr;
      }
      // mig 1280 (Rob / owner 2026-10-30): at least one of TM / RM / SD when the server switch is on
      const teamErr = teamPositionError(
        teamPositionRequired,
        hierarchyChain,
        isResume ? teamHadOneRef.current : null,
      );
      if (teamErr) {
        errs.team = teamErr;
      }
    }
    if (s === "basics") {
      if (!formData.title.trim()) {
        errs.title = "Enter an event title.";
      }
      if (!formData.type) {
        errs.type = "Choose an event type.";
      }
    }
    if (s === "when") {
      if (!formData.state) {
        errs.state = "Choose the regulatory state.";
      }
      if (!dateRange.startDate) {
        errs.startDate = "Choose a start date.";
      }
      if (!timezone) {
        errs.timezone = "Choose a time zone.";
      }
      if (!dateRange.allDay && !dateRange.startTime) {
        errs.startTime = "Set a start time (or mark the event All Day).";
      }
      // End date is required for multi-day types (gun shows, expos/conferences).
      if (isMultiDay && !dateRange.endDate) {
        errs.endDate = "Multi-day events need an end date.";
      }
      // Single-day timed events need an end time so the check-in window closes.
      if (!isMultiDay && !dateRange.allDay && !dateRange.endTime) {
        errs.endTime = "Set an end time (or mark the event All Day).";
      }
      // Surface each date sub-error under its own key so they show TOGETHER (not
      // first-only), matching the picker's multi-line error list. Don't clobber the
      // required-field messages already set above (they're clearer than the
      // range-validator's equivalents).
      const dateErrors = validateDateRange(dateRange);
      if (dateErrors.startDate && !errs.startDate) {
        errs.startDate = dateErrors.startDate;
      }
      if (dateErrors.endDate && !errs.endDate) {
        errs.endDate = dateErrors.endDate;
      }
      if (dateErrors.dateRange) {
        errs.dateRange = dateErrors.dateRange;
      }
      const dhPlan = planDayHoursSave(dailyHoursApp, dailyHours, []);
      if (dhPlan.action === "invalid") {
        errs.dailyHours = dhPlan.message;
      }
      // Location: require a state on physical/virtual/hybrid (regulatory).
      if (
        (formData.location_type === "physical" ||
          formData.location_type === "hybrid") &&
        !formData.location_state
      ) {
        errs.location_state = "Enter the location state.";
      }
      // Location NAME is required for in-person events: the self-check-in picker
      // (list_self_checkin_events_v2) needs a non-blank location_name, and a blank venue
      // reads as an incomplete event. (It auto-fills from a selected org/location, but the
      // operator must supply one when none is chosen.)
      if (
        (formData.location_type === "physical" ||
          formData.location_type === "hybrid") &&
        !formData.location_name.trim()
      ) {
        errs.location_name = "Enter a location / venue name.";
      }
      // Venue ORGANIZATION is required for in-person events (owner decision
      // 2026-09-09). The selector on this step was optional, which is why in-person
      // events reach PROD with no row in event_organizations / event_locations at
      // all — 13 of 53 range nights among them. Rule + exemptions (virtual, date
      // TBD) live in utils/eventVenueRequirement so the edit surface enforces the
      // identical thing. `grandfathered` is never set here: this is the CREATE
      // path, so there is no pre-existing event to retro-invalidate.
      const venueErr = eventVenueError({
        locationType: formData.location_type,
        eventType: formData.type,
        // The create wizard always writes date_tbd: false — stated explicitly so
        // this reads as a fact about the flow, not an omission.
        dateTbd: false,
        orgSelectionCount: orgLocationSelections.length,
        promoterId: formData.promoter_id,
        notAdvertised: formData.not_advertised,
      });
      if (venueErr) {
        errs[venueErr.field === "promoter" ? "promoter_id" : "org_location"] =
          venueErr.message;
      }
    }
    return errs;
  };

  // Once a step's Next has been attempted, keep its inline errors in sync with the
  // CURRENT normalized values by RE-VALIDATING on every relevant change. An error
  // clears only when its field actually becomes valid, and is RESTORED when a field
  // (or a dependent field — e.g. the times blanked when Start Date is cleared)
  // becomes invalid again, instead of being deleted just because a value changed.
  // Inert until the step is attempted, so errors never appear prematurely.
  useEffect(() => {
    if (attemptedSteps.has(basicsStep)) {
      setBasicsErrors(validateBasicsStep(basicsStep));
    }
    // validateBasicsStep is a pure function of the deps below; re-run on any change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // orgLocationSelections is a validated input too (venue requirement), so a
    // just-added organization must clear its error the same way typing a title does.
  }, [
    formData,
    dateRange,
    timezone,
    basicsStep,
    attemptedSteps,
    orgLocationSelections,
    hierarchyChain,
    teamPositionRequired,
    leadRequired,
    isSelfService,
  ]);

  const goBasicsNext = async () => {
    // Mark this step attempted so the re-validation effect keeps its errors live.
    setAttemptedSteps((prev) =>
      prev.has(basicsStep) ? prev : new Set(prev).add(basicsStep),
    );
    const errs = validateBasicsStep(basicsStep);
    if (Object.keys(errs).length > 0) {
      setBasicsErrors(errs);
      showToast("error", "Please fix the highlighted fields.");
      return;
    }
    setBasicsErrors({});
    const i = reachableBasics.indexOf(basicsStep);
    if (i < reachableBasics.length - 1) {
      setBasicsStep(reachableBasics[i + 1]);
      return;
    }
    // Last reachable step. Create flow: Save the event as incomplete and return
    // to the list (the remaining steps happen later via "Configure Event").
    // Configure flow: persist the Team edits and continue into EIB setup.
    if (isResume) {
      await createEventAndEnterSetup();
    } else {
      await createEventAndEnterSetup({ incomplete: true });
    }
  };

  const goBasicsBack = () => {
    const i = reachableBasics.indexOf(basicsStep);
    if (i > 0) {
      setBasicsStep(reachableBasics[i - 1]);
    }
  };

  // "Save & finish later" — from When & Where onward: validate the current step
  // (earlier steps were validated to reach here), persist the event marked
  // incomplete, and return to the list so someone can finish it later.
  const saveAndFinishLater = async () => {
    setAttemptedSteps((prev) =>
      prev.has(basicsStep) ? prev : new Set(prev).add(basicsStep),
    );
    const errs = validateBasicsStep(basicsStep);
    if (Object.keys(errs).length > 0) {
      setBasicsErrors(errs);
      showToast("error", "Please fix the highlighted fields before saving.");
      return;
    }
    setBasicsErrors({});
    await createEventAndEnterSetup({ incomplete: true });
  };

  // -----------------------------------------------------------------------
  // Event creation (basics → setup hand-off). Always `scheduled`, never draft.
  // -----------------------------------------------------------------------
  const buildStartEnd = (): { start_time: string; end_time: string } => {
    const startTzOffset = getTimezoneOffset(timezone, dateRange.startDate);
    const start_time = `${dateRange.startDate}T${
      dateRange.allDay ? "00:00" : dateRange.startTime || "00:00"
    }:00${startTzOffset}`;
    const endDateEff = dateRange.endDate || dateRange.startDate;
    const endTzOffset = getTimezoneOffset(timezone, endDateEff);
    const endTimeEff = dateRange.allDay
      ? "23:59"
      : dateRange.endTime || dateRange.startTime || "00:00";
    const end_time = `${endDateEff}T${endTimeEff}:00${endTzOffset}`;
    return { start_time, end_time };
  };

  // MSR QA 10-20: before saving, warn (never block) when the lead rep -- or, when configuring, any rep already on the
  // Sales Reps list -- has time off on the event's local date(s).
  const [timeOffPrompt, setTimeOffPrompt] = useState<{
    message: string;
    opts?: { incomplete?: boolean };
  } | null>(null);
  const createEventAndEnterSetup = async (opts?: { incomplete?: boolean }) => {
    // mig 1280: any save path (incl. "Save & finish later" before the Team step) needs a TM / RM / SD when the switch is on
    const teamErr = teamPositionError(
      teamPositionRequired,
      hierarchyChain,
      isResume ? teamHadOneRef.current : null,
    );
    if (teamErr) {
      setAttemptedSteps((prev) => new Set(prev).add("team"));
      setBasicsStep("team");
      setBasicsErrors({ team: teamErr });
      showToast("error", teamErr);
      return;
    }
    // mig 1630: and a Lead / Event Rep (or self-service) when the switch is on
    const leadErr = leadRequiredError(
      leadRequired,
      hierarchyChain.repId,
      isSelfService,
    );
    if (leadErr) {
      setAttemptedSteps((prev) => new Set(prev).add("team"));
      setBasicsStep("team");
      setBasicsErrors({ lead: leadErr });
      showToast("error", leadErr);
      return;
    }
    // mig 1760: switch off -> never blocks; say once, on save, what a missing lead means
    const softLead = leadSoftWarning(
      leadRequired,
      hierarchyChain.repId,
      isSelfService,
    );
    if (softLead) {
      showToast("warning", softLead);
    }
    try {
      const dates = datesInclusive(
        dateRange.startDate,
        dateRange.endDate || dateRange.startDate,
      );
      const lead = resolveLeadRepPositionId(
        hierarchyChain.repId,
        isSelfService,
      );
      const listed = createdEvent?.id
        ? await listEventSalesReps(createdEvent.id)
        : [];
      const ids = [
        ...(lead ? [lead] : []),
        ...listed.map((a) => a.sales_position_id),
      ];
      const off = await getTimeOffConflicts(ids, dates);
      const nameFor = (pos: string) => {
        const sp = (salesPositions ?? []).find((x) => x.id === pos);
        const l = listed.find((a) => a.sales_position_id === pos);
        return (
          sp?.assigned_name ||
          l?.sales_position?.assigned_name ||
          sp?.title ||
          l?.sales_position?.title ||
          "A rep"
        );
      };
      const lines = Object.entries(off).map(([pos, ds]) =>
        timeOffSentence(nameFor(pos), ds),
      );
      if (lines.length) {
        setTimeOffPrompt({ message: `${lines.join(" ")} Save anyway?`, opts });
        return;
      }
    } catch (e) {
      console.warn("time-off check skipped", e);
    }
    await doCreateEventAndEnterSetup(opts);
  };
  const doCreateEventAndEnterSetup = async (opts?: {
    incomplete?: boolean;
  }) => {
    // incomplete=true → "Save & finish later": persist the event marked
    // setup_incomplete and return to the list instead of entering EIB setup.
    // CFG-04 (owner 2026-10-12): only gun shows, range nights and seminars have setup. Any other type is never saved
    // as incomplete (the server forces the same, migration 510); it still returns to the list.
    const finishLater = opts?.incomplete === true || fieldManager;
    // A not-advertised gun show has nothing to set up (no invitations, no landing page): never "incomplete".
    // RM/TM (round-4 audit): their browser publishes no QR, so any event of theirs that gets a generic CTA waits on
    // the events team's incomplete list (and notice) until the QR is published; the database accepts that flag on a
    // non-setup type only for an RM/TM-created event (mig 1241).
    // Owner 2026-10-29: class, table, seminar and speaking engagement have no event CTA / QR, so never wait for one.
    const qrPending =
      fieldManager &&
      SUBSCRIPTION_ELIGIBLE_EVENT_TYPES.includes(formData.type) &&
      !eventTypeHasNoCtaQr(formData.type);
    const incomplete =
      finishLater &&
      ((eventTypeNeedsSetup(formData.type) && !formData.not_advertised) ||
        qrPending);
    // Re-entry guard: if the event was already created (e.g. a follow-up step
    // failed after creation, or we resumed via "Configure Event"), never create
    // a second one. Persist the Team/promoter/hierarchy edits made on the Team
    // step (idempotent — safe for both re-entry cases), then resume setup / exit.
    if (createdEvent) {
      setCreating(true);
      try {
        // MSR activation fix 6 (internal re-audit P2-3): the Lead/Event Rep is not part of this update. It changes
        // only when the Team step's rep differs from the event as created/loaded, through one compare-and-set call
        // that moves the Sales Reps list's Lead and the field together; or to retry a create-time sync that failed.
        const repPlan = planLeadRepSave(
          {
            repId: createdEvent.sales_position_id,
            isSelfService: createdEvent.is_self_service,
          },
          { repId: hierarchyChain.repId, isSelfService },
          leadSyncPendingRef.current,
        );
        let leadOk = true;
        const preMarkSelfService =
          !!repPlan.leadCall &&
          repPlan.leadCall.next === null &&
          repPlan.selfService === true;
        if (preMarkSelfService) {
          // mig 1630 (internal audit P1): mark self-service before clearing the lead ("lead required" refuses a clear
          // on an event that is not self-service)
          const { error: ssErr } = await supabase
            .from("events")
            .update({ is_self_service: true })
            .eq("id", createdEvent.id);
          if (ssErr) {
            throw ssErr;
          }
        }
        if (repPlan.leadCall) {
          const r = await syncLeadRepToSalesReps(
            createdEvent.id,
            repPlan.leadCall.next,
            repPlan.leadCall.expected,
          );
          leadOk = !r.error;
          if (!leadOk && preMarkSelfService) {
            // the rep stays, so does "not self-service"
            await supabase
              .from("events")
              .update({ is_self_service: false })
              .eq("id", createdEvent.id);
          }
          // A retry of the create-time sync (same rep, field already set by the insert) is the create case; a
          // changed rep is an edit (compare-and-set: refused = nothing changed).
          const notice = leadRepSyncNotice(r, {
            onCreate:
              !!repPlan.leadCall.next &&
              repPlan.leadCall.next === repPlan.leadCall.expected &&
              repPlan.leadCall.next === createdEvent.sales_position_id,
          });
          if (notice) {
            showToast(notice.type, notice.message);
          }
          if (leadOk) {
            leadSyncPendingRef.current = false;
          }
        }
        const { data: teamRows, error: teamErr } = await supabase
          .from("events")
          .update({
            promoter_id: formData.promoter_id || null,
            promoter_name: formData.promoter_name || null,
            ...(leadOk && repPlan.selfService !== undefined
              ? { is_self_service: repPlan.selfService }
              : {}),
            territory_manager_position_id: hierarchyChain.tmId || null,
            regional_manager_position_id: hierarchyChain.rmId || null,
            sales_director_position_id: hierarchyChain.sdId || null,
            ...(incomplete ? { setup_incomplete: true } : {}),
          })
          .eq("id", createdEvent.id)
          .select("id");
        if (teamErr) {
          throw teamErr;
        }
        // RLS answers a refused UPDATE with zero rows, not an error (internal audit P2-2): never report it as saved.
        if (!teamRows || teamRows.length === 0) {
          throw new Error(
            "the event was not updated (no permission to change it)",
          );
        }
        await saveDailyHours(createdEvent.id);
        if (leadOk && repPlan.leadCall) {
          setCreatedEvent({
            ...createdEvent,
            sales_position_id: repPlan.leadCall.next,
            ...(repPlan.selfService !== undefined
              ? { is_self_service: repPlan.selfService }
              : {}),
          } as Event);
        } else if (leadOk && repPlan.selfService !== undefined) {
          setCreatedEvent({
            ...createdEvent,
            is_self_service: repPlan.selfService,
          } as Event);
        }
      } catch (e) {
        console.warn("Saving team/promoter on configure failed:", e);
        setCreating(false);
        if (fieldManager) {
          // RM/TM cannot change an event after creating it: do not leave them on a step that cannot save
          showToast(
            "info",
            "The event is saved. Changes to it are made by the events team.",
          );
          navigate(`/events/${createdEvent.id}`);
          return;
        }
        showToast("error", "Couldn't save the team assignment — please retry.");
        return;
      }
      setCreating(false);
      if (finishLater) {
        showToast(
          "success",
          incomplete
            ? "Saved — anyone can finish setting it up from the events list."
            : "Saved.",
        );
        navigate("/events");
        return;
      }
      setPhase("setup");
      return;
    }
    if (!timezone) {
      // Defence in depth: the When & Where validation already refuses this.
      setBasicsStep("when");
      showToast("error", "Choose a time zone.");
      return;
    }
    setCreating(true);
    try {
      const { start_time, end_time } = buildStartEnd();

      const eventData: CreateEventInput & { id: string } = {
        // Client-supplied stable id → idempotent create-on-retry.
        id: eventIdRef.current,
        title: formData.title,
        description: formData.description || undefined,
        type: formData.type as Event["type"],
        // No draft, ever — created as scheduled at the basics → setup hand-off.
        status: "scheduled",
        // "Save & finish later" marks it incomplete (badged in the list); a normal
        // create-and-continue leaves it false.
        setup_incomplete: incomplete,
        // Owner picker removed from the create flow — default to the current
        // user (matches EventForm's default; the column is nullable).
        organizer_id: user?.id || undefined,
        state: formData.state || undefined,
        start_time,
        end_time,
        all_day: dateRange.allDay,
        date_tbd: false,
        timezone: timezone || undefined,
        location_type: formData.location_type,
        // Default the venue name to the associated org's name when the operator left it blank
        // (org-only associations with an org-level address otherwise leave location_name empty,
        // which hides the event from the self-check-in picker — list_self_checkin_events_v2
        // requires location_name <> '').
        location_name:
          formData.location_name ||
          orgLocationSelections[0]?.org_name ||
          undefined,
        location_address:
          formData.location_address_line1 ||
          formData.location_city ||
          formData.location_state ||
          formData.location_postal_code
            ? {
                address_line1: formData.location_address_line1 || undefined,
                city: formData.location_city || undefined,
                state: formData.location_state || undefined,
                postal_code: formData.location_postal_code || undefined,
              }
            : undefined,
        meeting_url: formData.meeting_url || undefined,
        // Item 8: gun-show entry fee (internal reimbursement tracking). Persist a number only when
        // it's a gun show and a value was entered; otherwise leave it unset (nullable column).
        entry_fee:
          isGunShow && formData.entry_fee.trim() !== ""
            ? Number(formData.entry_fee)
            : undefined,
        // Item 4: max capacity. Blank = uncapped. Hard-cap enforcement only when both a limit is set
        // AND the operator turned the hard-cap toggle on.
        max_attendees:
          formData.max_attendees.trim() !== ""
            ? Number(formData.max_attendees)
            : undefined,
        attendance_hard_cap:
          formData.max_attendees.trim() !== "" && formData.attendance_hard_cap,
        // Removed from the create flow → sensible defaults.
        is_public: false,
        is_featured: false,
        registration_required: registrationRequired,
        // Walk-ins default ALLOWED for all (redesign) — no toggle.
        allow_walk_ins: true,
        // Public self check-in ON by default for every new event (tester feedback 2026-08-15).
        // The DB column default is false, which surprised operators; enabling it here means
        // self-check-in is available without a manual toggle. WHEN it actually opens is still
        // gated server-side by self_checkin_event_active_today (1h before start for a timed
        // event, or day-of for an all-day event) — so this never opens check-in early.
        public_self_checkin_enabled: true,
        is_recurring: false,
        created_by: user?.id,
        promoter_id: formData.promoter_id || null,
        promoter_name: formData.promoter_name || null,
        // Sales hierarchy (self-service → null lead rep + flag).
        sales_position_id: resolveLeadRepPositionId(
          hierarchyChain.repId,
          isSelfService,
        ),
        is_self_service: isSelfService,
        territory_manager_position_id: hierarchyChain.tmId || null,
        regional_manager_position_id: hierarchyChain.rmId || null,
        sales_director_position_id: hierarchyChain.sdId || null,
        // Gun Show - Not Advertised (migration 1242).
        not_advertised: formData.type === "gun_show" && formData.not_advertised,
        // Auto-mint generic CTA for subscription-eligible types (as EventForm does).
        subscription_signup_eligible:
          SUBSCRIPTION_ELIGIBLE_EVENT_TYPES.includes(formData.type),
      } as CreateEventInput & { id: string };

      const newEvent = (await createMutation.mutateAsync(
        eventData as Omit<Event, "id" | "created_at" | "updated_at"> & {
          id: string;
        },
      )) as Event;

      // The event ROW now exists. Commit to the setup phase immediately so a
      // failure in the follow-up (best-effort) association/QR work can NEVER
      // strand the user on step 3 with an already-created event — which would
      // cause a duplicate on retry (internal-audit MED). Everything below is
      // best-effort and must not throw back into the create catch.
      setCreatedEvent(newEvent);

      // Hours each day: best-effort, the event already exists (the operator is told on failure).
      await saveDailyHours(newEvent.id);

      // MSR activation fix 6: the chosen Lead/Event Rep also goes on the event's Sales Reps list (the source of
      // truth for who works it). Never throws; the event already exists.
      if (newEvent.sales_position_id) {
        const r = await syncLeadRepToSalesReps(
          newEvent.id,
          newEvent.sales_position_id,
          newEvent.sales_position_id,
        );
        leadSyncPendingRef.current = !!r.error;
        const notice = leadRepSyncNotice(r, { onCreate: true });
        if (notice) {
          showToast(notice.type, notice.message);
        }
      }

      try {
        // Org / location associations (helper clears+rewrites; safe when empty;
        // throws on RLS/network — non-fatal here since the event already exists).
        //
        // THIS IS THE ONLY PLACE THE CREATE FLOW WRITES THE ASSOCIATION, and
        // Event-in-a-Box deliberately does NOT retry it. A guard that made the EIB
        // launch persist the selection was built and reverted (branch
        // fix/eib-persist-org-association) because it wrote nothing on any path
        // reachable today, and dead code that looks like coverage is worse than none:
        //   - The create flow ALWAYS ends here with setup_incomplete and returns to
        //     the list, so the embedded EIB setup phase is entered only by RESUME —
        //     where `reachableBasics` is [] and this step never renders, so the
        //     selection handed down is empty by design.
        //   - The other candidate source, EIB's `eventOrgId`, is READ FROM
        //     event_organizations by that wizard's mount effect, so writing it back
        //     is a tautology; it is null precisely on the events that lack a row.
        //     (Measured on PROD: all 15 EIB launches that anchored proximity on an
        //     organization already carried exactly that row.)
        // The real cause of unlinked events was that the venue selector on step 2 was
        // OPTIONAL — which is what the venue requirement above now fixes. If this
        // write fails the operator is told, and the association can be set on the
        // event's Overview tab.
        await saveEventOrgLocations(
          newEvent.id,
          orgLocationSelections.map((s) => ({
            org_id: s.org_id,
            location_id: s.location_id,
          })),
        );
      } catch (assocErr) {
        console.warn("Event org/location association failed:", assocErr);
        showToast(
          "error",
          "Event created, but saving its org/location links failed — you can set them on the event.",
        );
      }

      // The links above just prepared the generic CTA from the promoter / host (database, at
      // commit). Draw its QR and save its flyer BEFORE leaving this screen (owner 2026-09-28:
      // everything done by the time they leave the wizard). Never throws; bounded.
      // RM/TM (external audit 2007c2e4 #2): no QR / flyer from their browser -- a client-built QR link cannot be
      // verified by the server, so it is published by whoever configures the event (the CTA tab finishes it).
      if (
        SUBSCRIPTION_ELIGIBLE_EVENT_TYPES.includes(formData.type) &&
        user?.id &&
        !fieldManager
      ) {
        setFinishingCta(true);
        try {
          await afterEventLinksSaved(newEvent.id, user.id);
        } finally {
          setFinishingCta(false);
        }
      }

      // Best-effort: heads-up the events team that a new event was saved (right after
      // the create wizard completes, BEFORE configuration) so they can track/finish it.
      // Fired on every creation from 2026-08-26 (business intent); since CFG-04 (2026-10-12)
      // only for the types that have setup (see below). Fire-and-forget —
      // never blocks the save/navigate; the events list badges incomplete events too.
      // CFG-04 (owner 2026-10-12): only types that have a setup step (gun show, range night, seminar) "need
      // configuration"; a class, table, speaking engagement, conference, etc. sends no such notice.
      if (
        isFeatureEnabled("incompleteEventNotify") &&
        ((eventTypeNeedsSetup(newEvent.type ?? formData.type) &&
          !formData.not_advertised) ||
          qrPending)
      ) {
        void notifyEventNeedsConfiguration({
          qrPending,
          id: newEvent.id,
          title: newEvent.title,
          type: newEvent.type ?? null,
          startTime: newEvent.start_time ?? null,
          url: `${window.location.origin}/events/${newEvent.id}`,
        });
      }

      if (finishLater) {
        showToast(
          "success",
          incomplete
            ? "Saved as incomplete — anyone can finish setting it up from the events list."
            : "Event saved. This event type needs no setup.",
        );
        navigate("/events");
      } else {
        showToast("success", "Event created — now let's set it up.");
        setPhase("setup");
      }
    } catch (error) {
      console.error("Error creating event:", error);
      showToast(
        "error",
        error instanceof Error ? error.message : "Failed to create event",
      );
    } finally {
      setCreating(false);
    }
  };

  const goToEvent = () => {
    if (createdEvent) {
      navigate(`/events/${createdEvent.id}`);
    } else {
      navigate("/events");
    }
  };

  // -----------------------------------------------------------------------
  // SETUP phase — EventInABoxWizard rendered INLINE (embedded) so the create
  // flow reads as ONE continuous wizard: the parent supplies the page chrome +
  // the unified step rail, and the EIB's own modal chrome/rail are suppressed.
  // Its footer close advances to the optional CTA step — the "skip setup" lever.
  // -----------------------------------------------------------------------
  // Item 5: a resumed ("Configure Event") wizard skips the basics/team steps entirely and enters
  // the EIB setup directly — while the event hydrates (phase flips from the initial "basics" to
  // "setup"), show a loader instead of briefly flashing the create-only basics UI.
  //
  // Gate ONLY on the pre-hydration "basics" phase, not "!== setup": the EIB footer's "Skip setup"
  // advances a resumed wizard to the terminal "cta" phase (onClose → setPhase("cta")). The old
  // `phase !== "setup"` guard re-matched there and spun forever with no console error — the CTA
  // block below was never reached. "setup" and "cta" are both legitimate post-hydration phases.
  if (isResume && phase === "basics") {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
      </div>
    );
  }
  if (phase === "setup" && createdEvent) {
    const initialMode = registrationRequired ? "pre_register" : "show_up";
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/events")}
            className="text-gray-600 hover:text-gray-900"
            type="button"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isResume ? "Configure Event" : "Create New Event"}
          </h1>
        </div>

        <StepRail
          phase="setup"
          basicsStep={basicsStep}
          eibStepIndex={eibStepIndex}
          eibStepTotal={eibStepTotal}
          isResume={isResume}
        />

        <EventInABoxWizard
          isOpen
          embedded
          initialMode={initialMode}
          onStepChange={(index, total) => {
            setEibStepIndex(index);
            setEibStepTotal(total);
          }}
          event={{
            id: createdEvent.id,
            title: createdEvent.title,
            start_date: createdEvent.start_time ?? null,
            type: createdEvent.type ?? null,
          }}
          onClose={() => setPhase("cta")}
          onLaunched={() => {
            /* EIB keeps its log visible; the operator advances via its footer. */
          }}
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // CTA / QR phase (step 8) — EventCTAInitPanel; "Not now" is a persisted deferral.
  // -----------------------------------------------------------------------
  if (phase === "cta") {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <StepRail
          phase="cta"
          basicsStep={basicsStep}
          eibStepIndex={eibStepIndex}
          eibStepTotal={eibStepTotal}
          isResume={isResume}
        />
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircleIcon className="h-6 w-6" />
            <h2 className="text-lg font-semibold">Event is ready</h2>
          </div>
          {/* Step 8: prepare the event's subscription CTA ($20 voucher + QR) with tracking codes
              copied from the promoter/host. Rendered only once the event row exists, so its
              org/location links (saved right after create) are what the server resolves from. */}
          {createdEvent && !eventTypeIsNoPromo(createdEvent.type) ? (
            <EventCTAInitPanel event={createdEvent} surface="wizard" />
          ) : null}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Button variant="primary" onClick={goToEvent}>
              Finish — go to event
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // BASICS phase (steps 1-3)
  // -----------------------------------------------------------------------
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <ConfirmDialog
        isOpen={timeOffPrompt !== null}
        onClose={() => setTimeOffPrompt(null)}
        onConfirm={() => {
          const o = timeOffPrompt?.opts;
          setTimeOffPrompt(null);
          void doCreateEventAndEnterSetup(o);
        }}
        title="A rep isn't marked available"
        message={timeOffPrompt?.message ?? ""}
        confirmText="Save anyway"
        variant="warning"
      />
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/events")}
          className="text-gray-600 hover:text-gray-900"
          type="button"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isResume ? "Configure Event" : "Create New Event"}
        </h1>
      </div>

      <StepRail
        phase="basics"
        basicsStep={basicsStep}
        eibStepIndex={eibStepIndex}
        eibStepTotal={eibStepTotal}
        isResume={isResume}
      />

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* STEP 1 — BASICS */}
        {basicsStep === "basics" && (
          <>
            {/* Title / description */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Basic Information
              </h3>
              <Input
                label="Event Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Enter event title"
                error={basicsErrors.title}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Event description"
                />
              </div>

              {/* Pre-registration is chosen later on the EIB "Workflow" step —
                  the redundant page-1 "Pre-registration required?" toggle was
                  removed. Whether the event is registration-based defaults from
                  the event type's classification. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={
                    formData.not_advertised
                      ? GUN_SHOW_NOT_ADVERTISED
                      : formData.type
                  }
                  onChange={handleTypeChange}
                  required
                  aria-invalid={!!basicsErrors.type}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500 ${
                    basicsErrors.type
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Select event type...</option>
                  {classifiedTypeOptions.map((et) => (
                    <option key={et.value} value={et.value}>
                      {et.label}
                    </option>
                  ))}
                </select>
                {basicsErrors.type && (
                  <p className="mt-1 text-sm text-red-600">
                    {basicsErrors.type}
                  </p>
                )}
              </div>

              {/* Item 8: gun-show entry fee — optional, internal tracking/reimbursement only. */}
              {isGunShow && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entry Fee{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      name="entry_fee"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={formData.entry_fee}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          entry_fee: e.target.value,
                        }))
                      }
                      placeholder="0.00"
                      className="w-full rounded-md border border-gray-300 py-2 pl-7 pr-3 focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    For internal tracking and reimbursement only — not charged
                    to attendees.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* STEP 2 — WHEN & WHERE */}
        {basicsStep === "when" && (
          <>
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Date & Time</h3>
              <p className="text-sm text-gray-500">
                {isMultiDay
                  ? "This event type spans multiple days — set a start and end date."
                  : "Single-day event — set the date with a start and end time."}
              </p>
              {/* Required-field errors (start/end date & time) are passed into the
                  picker so each renders field-level with aria-invalid on its own
                  control; the picker also shows cross-field ordering errors
                  (dateRange) via its internal validation. */}
              <DateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                lockEndDate={!isMultiDay}
                externalErrors={{
                  startDate: basicsErrors.startDate,
                  startTime: basicsErrors.startTime,
                  endDate: basicsErrors.endDate,
                  endTime: basicsErrors.endTime,
                }}
              />
              {/* Cross-field ordering errors (dateRange) are rendered by the
                  picker's own internal validation, so we don't repeat them here. */}
              {!isMultiDay && (
                <p className="text-xs text-gray-500 -mt-2">
                  This is a single-day event type — the end date is locked to
                  the start date. Only the start and end <em>times</em> are
                  editable. (Choose a multi-day type like Gun Show or Conference
                  to set a separate end date.)
                </p>
              )}
              <div>
                <TimezoneSelector
                  value={timezone}
                  hint={describeTimezoneSource(tzAuto?.resolved ?? null)}
                  note={timezoneUnsetNote(tzAuto)}
                  onChange={(tz) => {
                    if (!tz) {
                      // Back to "Select timezone...": hand the zone back to the ZIP rule.
                      timezoneManuallySet.current = false;
                      setTzRederive((n) => n + 1);
                      return;
                    }
                    setTimezone(tz);
                    setTzAuto(null);
                    timezoneManuallySet.current = true;
                  }}
                  required={!!dateRange.startDate}
                />
                {basicsErrors.timezone && (
                  <p className="mt-1 text-sm text-red-600">
                    {basicsErrors.timezone}
                  </p>
                )}
              </div>
              <EventDailyHoursEditor
                app={dailyHoursApp}
                value={dailyHours}
                onChange={setDailyHours}
                saved={[]}
              />
              {basicsErrors.dailyHours && (
                <p className="-mt-2 text-sm text-red-600">
                  {basicsErrors.dailyHours}
                </p>
              )}
            </div>

            {/* Regulatory state */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Regulatory State <span className="text-red-500">*</span>
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                autoComplete="off"
                aria-invalid={!!basicsErrors.state}
                className={`w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500 ${
                  basicsErrors.state
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300"
                }`}
              >
                <option value="">Select state...</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} - {s.name}
                  </option>
                ))}
              </select>
              {basicsErrors.state ? (
                <p className="mt-1 text-sm text-red-600">
                  {basicsErrors.state}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  Required for insurance/regulatory purposes and Call to Action
                  URLs
                </p>
              )}
            </div>

            {/* Item 4: soft max capacity — collected here on page 2, also shown on the Operations tab.
                NOT the same question as EIB's capacity box, despite both reading as
                "how many people". Verified against live PROD 2026-09-09:
                  - This writes `events.max_attendees` + `events.attendance_hard_cap`.
                    It is the ONLY writer of attendance_hard_cap anywhere, and that flag
                    is what the attendee trigger, the CHECK constraint and all three
                    admission guards gate on.
                  - EIB "Maximum tickets to issue" (pre-register + open) writes
                    `event_ticketing_provisioning.open_issuance_cap` — a TOKEN-MINTING cap
                    at a different chokepoint. `eib_apply_workflow_settings` returns from
                    its pre_register branch without touching events.max_attendees at all,
                    and PROD range nights really do carry different numbers in the two
                    (67/70, 45/70, 150/70), so operators are answering two questions.
                  - EIB "How many people do you expect?" (show-up / walk-in-only) DOES
                    collide with this field: it sets capacity + walk_in_limit outright and
                    max_attendees = GREATEST(old, new) — and when left BLANK it NULLs all
                    three, silently discarding whatever was typed here. That is a real
                    defect (it can leave attendance_hard_cap = true with no limit to
                    enforce) but it is a DB-side fix, not a reason to drop this field. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Capacity{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                type="number"
                name="max_attendees"
                min="1"
                step="1"
                inputMode="numeric"
                value={formData.max_attendees}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    max_attendees: e.target.value,
                  }))
                }
                placeholder="No limit"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave blank for no limit. By default this is a soft cap
                (informational only). Turn on the hard cap below to actually
                block sign-ups once the limit is reached.
              </p>
              <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.attendance_hard_cap}
                  disabled={formData.max_attendees.trim() === ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      attendance_hard_cap: e.target.checked,
                    }))
                  }
                  className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                />
                <span>
                  <span className="font-medium">Enforce as a hard cap</span> —
                  block new registrations, self check-ins, and walk-ins once the
                  limit is reached (people see &ldquo;the event attendance limit
                  has been reached&rdquo;). Enter a limit above to enable.
                </span>
              </label>
            </div>

            {/* Org / venue slot (gun-show swap). REQUIRED for in-person events —
                a gun show's link is its promoter (its org selector is swapped
                out), everything else links through organizations/locations. */}
            {isGunShow ? (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Promoter Organization{" "}
                  {venueLinkIsRequired && (
                    <span className="text-red-500">*</span>
                  )}
                </h3>
                <p className="text-sm text-gray-500">
                  Gun shows are run by a promoter. The promoter&apos;s address
                  is not the show location — enter the show address under
                  Location below.
                </p>
                <div
                  aria-invalid={!!basicsErrors.promoter_id}
                  className={
                    basicsErrors.promoter_id
                      ? "rounded-md border border-red-500 p-3"
                      : undefined
                  }
                >
                  <PromoterSelector
                    value={formData.promoter_id || null}
                    onChange={(promoterId, promoterName) =>
                      setFormData((prev) => ({
                        ...prev,
                        promoter_id: promoterId || "",
                        promoter_name: promoterName || "",
                      }))
                    }
                    allowAddUnsigned={canAddUnsignedPromoter(user?.role)}
                    defaultState={
                      formData.location_state || formData.state || null
                    }
                  />
                </div>
                {basicsErrors.promoter_id && (
                  <p className="mt-1 text-sm text-red-600">
                    {basicsErrors.promoter_id}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Associated Organizations & Locations{" "}
                  {venueLinkIsRequired && (
                    <span className="text-red-500">*</span>
                  )}
                </h3>
                <p className="text-sm text-gray-500">
                  {venueLinkIsRequired
                    ? "In-person events must be associated with the organization (or specific location) hosting them."
                    : "Associate this event with organizations or specific locations"}
                </p>
                <div
                  aria-invalid={!!basicsErrors.org_location}
                  className={
                    basicsErrors.org_location
                      ? "rounded-md border border-red-500 p-3"
                      : undefined
                  }
                >
                  <EventOrganizationLocationSelector
                    selected={orgLocationSelections}
                    onChange={setOrgLocationSelections}
                  />
                </div>
                {basicsErrors.org_location && (
                  <p className="mt-1 text-sm text-red-600">
                    {basicsErrors.org_location}
                  </p>
                )}
              </div>
            )}

            {/* Location block */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Location</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Type
                </label>
                <select
                  name="location_type"
                  value={formData.location_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="physical">Physical</option>
                  <option value="virtual">Virtual</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              {(formData.location_type === "physical" ||
                formData.location_type === "hybrid") && (
                <>
                  <Input
                    label="Location Name"
                    name="location_name"
                    value={formData.location_name}
                    onChange={handleChange}
                    placeholder="e.g., Conference Center"
                    helperText="This is how the venue is named in invitation emails (e.g. “Range Night at …”). Use its everyday name, without Inc/LLC or “Range Night”."
                    required
                    error={basicsErrors.location_name}
                  />
                  <Input
                    label="Address"
                    name="location_address_line1"
                    value={formData.location_address_line1}
                    onChange={handleChange}
                    placeholder="Street address"
                  />
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      label="City"
                      name="location_city"
                      value={formData.location_city}
                      onChange={handleChange}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="location_state"
                        value={formData.location_state}
                        onChange={handleChange}
                        required
                        aria-invalid={!!basicsErrors.location_state}
                        className={`w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500 ${
                          basicsErrors.location_state
                            ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300"
                        }`}
                      >
                        <option value="">Select state...</option>
                        {US_STATES.map((s) => (
                          <option key={s.code} value={s.code}>
                            {s.code} - {s.name}
                          </option>
                        ))}
                      </select>
                      {basicsErrors.location_state && (
                        <p className="mt-1 text-sm text-red-600">
                          {basicsErrors.location_state}
                        </p>
                      )}
                    </div>
                    <Input
                      label="Postal Code"
                      name="location_postal_code"
                      value={formData.location_postal_code}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}

              {(formData.location_type === "virtual" ||
                formData.location_type === "hybrid") && (
                <Input
                  label="Meeting URL"
                  name="meeting_url"
                  value={formData.meeting_url}
                  onChange={handleChange}
                  placeholder="https://zoom.us/..."
                />
              )}

              {formData.location_type === "virtual" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="location_state"
                    value={formData.location_state}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select state...</option>
                    {US_STATES.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.code} - {s.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Required for insurance/legal purposes
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* STEP 3 — TEAM */}
        {basicsStep === "team" && (
          <>
            <SalesHierarchyFields
              salesPositions={salesPositions ?? []}
              hierarchyChain={hierarchyChain}
              isSelfService={isSelfService}
              fieldManagerScope={
                fieldManager ? (fieldManagerScope ?? null) : null
              }
              onChange={(next, selfService) => {
                hierarchyManuallyOverridden.current = true;
                const managersEdited =
                  next.tmId !== hierarchyChain.tmId ||
                  next.rmId !== hierarchyChain.rmId ||
                  next.sdId !== hierarchyChain.sdId;
                if (managersEdited) {
                  managersHandEditedRef.current = true;
                }
                // GS-06: a new Lead/Event Rep refills the manager levels from that
                // rep's reporting chain (still editable afterwards).
                const leadChanged =
                  !!next.repId && next.repId !== hierarchyChain.repId;
                setHierarchyChain(
                  leadChanged
                    ? {
                        ...next,
                        ...fillFromLeadChain(
                          salesPositions ?? [],
                          next.repId,
                          next,
                          managersHandEditedRef.current
                            ? null
                            : accountLevelsRef.current,
                        ).levels,
                      }
                    : next,
                );
                setIsSelfService(selfService);
              }}
              note={
                <SalesHierarchyChainNote
                  salesPositions={salesPositions ?? []}
                  leadId={isSelfService ? null : hierarchyChain.repId}
                  current={hierarchyChain}
                  eventId={resumeEventId ?? null}
                  onUseLeadChain={(levels) => {
                    hierarchyManuallyOverridden.current = true;
                    setHierarchyChain((prev) => ({ ...prev, ...levels }));
                  }}
                />
              }
            />
            {basicsErrors.team && (
              <p
                className="text-sm text-red-600"
                data-testid="team-position-required"
                role="alert"
              >
                {basicsErrors.team}
              </p>
            )}
            {basicsErrors.lead && (
              <p
                className="text-sm text-red-600"
                data-testid="lead-rep-required"
                role="alert"
              >
                {basicsErrors.lead}
              </p>
            )}
            {!basicsErrors.lead &&
              leadSoftWarning(
                leadRequired,
                hierarchyChain.repId,
                isSelfService,
              ) && (
                <p
                  className="text-sm text-yellow-800"
                  data-testid="lead-rep-soft-warning"
                >
                  No lead rep chosen. You can continue, but without a lead
                  nobody can start or close this event's days, and managers
                  won't be alerted if a day isn't closed.
                </p>
              )}

            {/* Non-gun-show optional promoter/sponsor selector (gun-show promoter
                is set on the previous step's org slot). */}
            {!isGunShow && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Promoter Organization
                </h3>
                <p className="text-sm text-gray-500">
                  Optionally select the promoter, sponsor, or partner
                  organization for this event
                </p>
                <PromoterSelector
                  value={formData.promoter_id || null}
                  onChange={(promoterId, promoterName) =>
                    setFormData((prev) => ({
                      ...prev,
                      promoter_id: promoterId || "",
                      promoter_name: promoterName || "",
                    }))
                  }
                />
              </div>
            )}

            <p className="text-xs text-primary-700 bg-primary-50 border border-primary-100 rounded px-3 py-2">
              Continuing saves this assignment and opens the guided setup for
              its reward, registration, audience and invitation.
            </p>
          </>
        )}
      </div>

      {/* Nav */}
      <div className="flex justify-between pt-2">
        <div>
          {reachableBasics.indexOf(basicsStep) > 0 && (
            <Button
              type="button"
              variant="secondary"
              onClick={goBasicsBack}
              disabled={creating}
            >
              Back
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate("/events")}
            disabled={creating}
          >
            Cancel
          </Button>
          {/* In configure mode, offer an explicit save-and-exit alongside the
              continue-into-setup primary. In create mode the primary IS Save. */}
          {isResume && (
            <Button
              type="button"
              variant="secondary"
              onClick={saveAndFinishLater}
              disabled={creating}
              title={
                eventTypeNeedsSetup(formData.type)
                  ? "Save this event now and finish setting it up later — it will be badged Incomplete in the events list."
                  : "Save this event now. This event type needs no setup, so it is not marked Incomplete."
              }
            >
              {creating ? "Saving..." : "Save & finish later"}
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            onClick={goBasicsNext}
            disabled={creating}
          >
            {creating
              ? finishingCta
                ? "Preparing QR code & flyer..."
                : "Saving..."
              : reachableBasics.indexOf(basicsStep) ===
                  reachableBasics.length - 1
                ? isResume
                  ? "Save & continue"
                  : "Save"
                : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step rail — a lightweight 1..9 overview across the whole flow.
// ---------------------------------------------------------------------------
// One continuous rail across BOTH stages of the create wizard. Steps 4-9 are the
// embedded EIB steps (mode/award/modeDetail/audience/invite/preview) — so the flow
// reads as a single wizard, not a first one that jumps into a second.
const BASICS_LABELS = ["Basics", "When & Where", "Team"];
// The six embedded EIB steps, in order. Walk-in-only reports only the first three
// (Workflow/Award/Check-in) — Audience/Invitation/Review & Launch are hidden.
const EIB_LABELS = [
  "Workflow",
  "Award",
  "Check-in / Registration",
  "Audience",
  "Invitation",
  "Review & Launch",
];
const CTA_LABEL = "CTA / QR";

const StepRail: React.FC<{
  phase: Phase;
  basicsStep: BasicsStep;
  eibStepIndex: number;
  // How many EIB steps the embedded wizard currently exposes (6 normally, 3 for
  // walk-in-only). Drives which steps appear and how the rail renumbers.
  eibStepTotal: number;
  // Configure mode ("Configure Event") shows only the remaining steps (Team
  // onward); the initial create wizard shows only Basics + When & Where.
  isResume: boolean;
}> = ({ phase, basicsStep, eibStepIndex, eibStepTotal, isResume }) => {
  // One continuous rail: 3 basics + the exposed EIB steps + CTA. Walk-in-only drops
  // the last three EIB labels, so everything after Check-in renumbers automatically.
  const eibCount = Math.min(Math.max(eibStepTotal, 1), EIB_LABELS.length);
  const steps = [...BASICS_LABELS, ...EIB_LABELS.slice(0, eibCount), CTA_LABEL];
  // Determine the 1-based active step index across the unified flow.
  let active: number;
  if (phase === "basics") {
    active = BASICS_ORDER.indexOf(basicsStep) + 1; // 1..3
  } else if (phase === "setup") {
    // EIB step 0..(eibCount-1) → unified 4..(3+eibCount) (after the 3 basics steps).
    active = 3 + Math.min(Math.max(eibStepIndex, 0), eibCount - 1) + 1;
  } else {
    active = steps.length; // CTA / QR (last step)
  }
  // Item 5: Create shows Basics + When & Where + Team (steps 1-3). Configure shows Workflow onward
  // (step 4+), since Team is now collected during Create.
  const railStart = isResume ? 3 : 0;
  const railEnd = isResume ? steps.length : 3;
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      {steps.slice(railStart, railEnd).map((label, i) => {
        const n = railStart + i + 1;
        const isActive = n === active;
        const isDone = n < active;
        return (
          <span
            key={label}
            className={`px-2 py-1 rounded ${
              isActive
                ? "bg-primary-600 text-white"
                : isDone
                  ? "bg-gray-200 text-gray-700"
                  : "bg-gray-50 text-gray-400"
            }`}
          >
            {n}. {label}
          </span>
        );
      })}
    </div>
  );
};

export default EventCreateWizard;
